// The grant register: who may do what, from when, until when.
//
// AUTHORITY IS DATA, WITH DATES. Not a job title, not an app role, not a list in
// source code. A cover — the QA manager on extended leave, a contractor in the
// seat — is two rows and no deploy: an end date on the outgoing grants, a start
// date on the incoming ones, and a granter on each.
//
// WHY THIS PAGE DRAWS A TIMELINE AND HUNTS FOR GAPS. At a handover there are two
// possible mistakes and they are not symmetric. An OVERLAP is harmless: for a few
// days two people may release product. A GAP is not: for that window NOBODY holds
// RELEASE, and held product strands at exactly the moment nobody is sure who to
// ask. A register shown as a list sorted by name hides that completely — the two
// rows look fine individually, and the hole only exists between them. So coverage
// is computed per ACTION across every holder, and a gap is reported as a defect.
import { Fragment, useCallback, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { api } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { Saved } from '@/components/Saved';
import {
  grantHint,
  localDate,
  localInstant,
  officeFocus,
  rows,
  serverMessage,
  useMyGrants,
  useResource,
  type AuthAction,
} from '../../shells/OfficeShell';
import { ProductionRoster, type RosterGrantAction } from './ProductionRoster';
import { ACTION_LABEL, RETIRED_ACTIONS, actionWithCode } from '../../lib/vocabulary';

interface Grant {
  id: number;
  upn: string;
  action: AuthAction;
  effectiveFrom: string;
  effectiveTo: string | null;
  authorisedByUpn: string;
  notes: string | null;
}

const ACTIONS: AuthAction[] = [
  'CAPTURE',
  'SPEC_APPROVE',
  'RELEASE',
  'CONCESSION',
  'ASSESS',
  'METER_ADMIN',
  'AUTH_ADMIN',
  'VERIFY',
  'ARTWORK_SIGNOFF',
  'RECEIVE',
  'COMPLAINT_TRIAGE',
  'DISPOSITION_RD',
  'DISPOSITION_MFG',
  'DISPOSITION_LOGISTICS',
];

/**
 * How much a gap in each action matters (UI/UX audit, 31 Oct 2026, UX-14).
 *
 * WHY THE COVERAGE BOX WAS ALWAYS AMBER. It alarmed on every action nobody held, and CONCESSION
 * has never been granted to anyone, so the box said so on every load: exactly the alarm the
 * comment on `registerRead` below warns people learn to ignore, hiding a real RELEASE gap behind
 * a permanent one. Actions are not equal. Without CAPTURE, VERIFY, RELEASE, RECEIVE,
 * SPEC_APPROVE, AUTH_ADMIN or ASSESS a daily job stops (readings, start-up sign-off, certificate
 * decisions, the dock, spec approval, granting, assessing). CONCESSION and METER_ADMIN are asked
 * for when the occasion arises: nobody holding them is worth naming, never worth an alarm.
 * ARTWORK_SIGNOFF is retired (artwork is app-magic's since 26 Oct 2026), so it is not a coverage
 * question at all.
 *
 * ⚠ NOT A RULE 14 MIRROR. A Record keyed by AuthAction rather than a subset list, so the compiler
 * refuses a build in which a new action is left unclassified: the vocabulary test only reads the
 * lists it is told about (MIRRORS in api/src/__tests__/vocabulary.test.ts), and this one does not
 * need to be there because the type already checks it. `ACTIONS` above stays the mirror.
 */
const COVERAGE: Record<AuthAction, 'must' | 'on-demand' | 'retired'> = {
  CAPTURE: 'must',
  VERIFY: 'must',
  RELEASE: 'must',
  RECEIVE: 'must',
  SPEC_APPROVE: 'must',
  AUTH_ADMIN: 'must',
  ASSESS: 'must',
  CONCESSION: 'on-demand',
  METER_ADMIN: 'on-demand',
  ARTWORK_SIGNOFF: 'retired',
  COMPLAINT_TRIAGE: 'on-demand',
  // Read by nothing until the hold matrix gate (plan step 5); asked for when a hold needs them.
  DISPOSITION_RD: 'on-demand',
  DISPOSITION_MFG: 'on-demand',
  DISPOSITION_LOGISTICS: 'on-demand',
};

/** How far ahead "every grant ends" is said while the action is still held (UX-14). */
const LOOK_AHEAD_MS = 14 * 86_400_000;

/**
 * When the last grant of an action ends, or null when one is open-ended or none is dated. Held
 * now and ending within LOOK_AHEAD_MS is the gap that has not opened yet: said while there is
 * still time to make the next grant, not on the morning nobody can act (UX-14).
 */
export function lastEnd(grants: Array<Pick<Grant, 'effectiveFrom' | 'effectiveTo'>>): number | null {
  const iv = intervalsOf(grants);
  if (iv.length === 0) return null;
  const end = Math.max(...iv.map((i) => i.to));
  return Number.isFinite(end) ? end : null;
}

/** Open-ended grants have no end. For drawing, stop the axis a quarter past today. */
const OPEN_ENDED_HORIZON_MS = 90 * 86_400_000;

interface Interval {
  from: number;
  to: number;
}

function intervalsOf(grants: Array<Pick<Grant, 'effectiveFrom' | 'effectiveTo'>>): Interval[] {
  return grants
    .map((g) => ({
      from: Date.parse(g.effectiveFrom),
      to: g.effectiveTo ? Date.parse(g.effectiveTo) : Number.POSITIVE_INFINITY,
    }))
    .filter((i) => Number.isFinite(i.from))
    .sort((a, b) => a.from - b.from);
}

/**
 * Windows in which NOBODY holds this action.
 *
 * Merges every holder's grant into one coverage line and reports the holes. A
 * grant that merely touches the next one at the same instant leaves no hole; one
 * that ends the day before the next starts leaves a day in which the action
 * cannot be exercised by anyone.
 */
function coverageGaps(grants: Grant[]): Interval[] {
  const iv = intervalsOf(grants);
  if (iv.length === 0) return [];
  const gaps: Interval[] = [];
  let covered = iv[0].to;
  for (let i = 1; i < iv.length; i++) {
    if (iv[i].from > covered) gaps.push({ from: covered, to: iv[i].from });
    covered = Math.max(covered, iv[i].to);
  }
  return gaps;
}

function coveredAt(grants: Grant[], at: number): boolean {
  return intervalsOf(grants).some((i) => i.from <= at && at < i.to);
}

function day(ms: number): string {
  if (!Number.isFinite(ms)) return 'open';
  return localDate(ms) ?? 'open';
}

/**
 * What the coverage strip spans, in words (UI/UX audit, 31 Oct 2026, UX-51).
 *
 * It used to print the AXIS: "2026-09-17 → 2027-01-29", and 2027-01-29 is only where the drawing
 * stops (OPEN_ENDED_HORIZON_MS past today), not a date anything ends. Read beside a coverage strip
 * it said every grant of the action ran out in January. So the words are the grants' own: from the
 * first start, to the last end, or "open" while any grant has no end (lastEnd's rule). Gaps between
 * are not this line's job: the Coverage box names them.
 */
export function coverageSpan(grants: Array<Pick<Grant, 'effectiveFrom' | 'effectiveTo'>>): string {
  const iv = intervalsOf(grants);
  if (iv.length === 0) return 'no dated grant';
  const end = lastEnd(grants);
  return end === null ? `from ${day(iv[0].from)}, open` : `${day(iv[0].from)} → ${day(end)}`;
}

/**
 * The instant an "End grant" posts as effectiveTo (UI/UX audit, 31 Oct 2026, UX-33), or null for
 * "now", which the route takes as the server's clock.
 *
 * ⚠ THE END OF THE CHOSEN DAY, NOT ITS START. The audit's sketch defaulted to "today at local
 * midnight", and that midnight has already passed for the whole working day: a grant ended at
 * 10am would be recorded as having stopped at 00:00, so every reading or decision made under it
 * this morning would sit in the register as made by somebody who held no authority at the time.
 * Ending a grant must never rewrite whether the past was authorised, so a day means "in force to
 * the end of that day" (the next local midnight, the same local-time rule as `localInstant`), and
 * the screen refuses a day already gone. "Now" is the immediate stop, for a person who must not
 * act again today.
 */
export function endInstant(when: 'now' | 'day', dateInput: string): string | null | undefined {
  if (when === 'now') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateInput.trim());
  if (!match) return undefined;
  const [, y, m, d] = match;
  const at = new Date(Number(y), Number(m) - 1, Number(d) + 1);
  return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
}

/**
 * Whether anybody else still holds the action at the instant this grant would stop (UX-33). A
 * warning, never a block: ending the last RELEASE or AUTH_ADMIN grant is sometimes right (a person
 * leaves), but it is the gap this page exists to catch, so the confirm panel says it first. Ending
 * the last AUTH_ADMIN in particular leaves nobody able to grant anything again from the app.
 */
export function heldByOthersAt(
  grants: Array<Pick<Grant, 'id' | 'action' | 'effectiveFrom' | 'effectiveTo'>>,
  ending: Pick<Grant, 'id' | 'action'>,
  at: number,
): boolean {
  return intervalsOf(grants.filter((g) => g.action === ending.action && g.id !== ending.id)).some(
    (i) => i.from <= at && at < i.to,
  );
}

function dayOf(iso: string | null): string {
  if (!iso) return 'open';
  return localDate(iso) ?? iso;
}

/** A grant as the register lists it, with the state it is in now. */
interface LedgerRow extends Grant {
  state: 'in force' | 'future' | 'lapsed';
}

const gcol = createColumnHelper<LedgerRow>();
const baseLedgerColumns = [
  gcol.accessor('upn', { header: 'Account', meta: { rowHeader: true } }),
  gcol.accessor('action', { header: 'Action', meta: { nowrap: true } }),
  gcol.accessor((g) => dayOf(g.effectiveFrom), { id: 'from', header: 'From', meta: { mono: true, nowrap: true } }),
  gcol.accessor((g) => dayOf(g.effectiveTo), { id: 'to', header: 'To', meta: { mono: true, nowrap: true } }),
  // A lapsed grant stays in the register. It is the evidence that the authority
  // once existed and when it ended.
  gcol.accessor('state', {
    header: 'State',
    cell: (c) => (
      <span
        className={`font-semibold ${c.getValue() === 'in force' ? 'text-(--ok)' : 'text-(--muted)'}`}
      >
        {c.getValue()}
      </span>
    ),
  }),
  gcol.accessor('authorisedByUpn', { header: 'Granted by' }),
  gcol.accessor((g) => g.notes ?? '', { id: 'notes', header: 'Notes', cell: (c) => c.getValue() || '—' }),
];

/**
 * The register's columns, with "End grant" on each grant still in force or yet to start, for an
 * AUTH_ADMIN holder only (UI/UX audit, 31 Oct 2026, UX-33). The page told people to overlap
 * grants at a handover, and the API has always ended a grant (POST /authorisations with an id:
 * EffectiveTo brought forward, never deleted, AUTHORISATION_REVOKED written), but nothing here
 * called it, so a handover could be started in the app and never finished. A lapsed grant has
 * nothing to end. Without AUTH_ADMIN the column is not drawn at all: the route refuses, and a row
 * of dead buttons is noise on a page everybody may read.
 */
function ledgerColumnsFor(canEnd: boolean, onEnd: (g: LedgerRow) => void) {
  if (!canEnd) return baseLedgerColumns;
  return [
    ...baseLedgerColumns,
    gcol.display({
      id: 'end',
      header: () => <span className="sr-only">End grant</span>,
      cell: (c) =>
        c.row.original.state === 'lapsed' ? null : (
          <Button
            variant="outline"
            size="sm"
            type="button"
            id={`end-grant-${c.row.original.id}`}
            onClick={() => onEnd(c.row.original)}
          >
            End grant
          </Button>
        ),
    }),
  ];
}

/** A shared axis for one action, so every bar under it is comparable. */
function axisFor(grants: Grant[], now: number) {
  const iv = intervalsOf(grants);
  const froms = iv.map((i) => i.from);
  const tos = iv.map((i) => (Number.isFinite(i.to) ? i.to : now + OPEN_ENDED_HORIZON_MS));
  const lo = Math.min(now, ...froms);
  const hi = Math.max(now + OPEN_ENDED_HORIZON_MS, ...tos);
  const span = hi - lo || 1;
  return {
    lo,
    hi,
    // Non-finite input clamps to the axis start rather than propagating NaN into
    // an SVG attribute. Callers still decide whether to draw at all (see the
    // `dated` guard on the grant rows); this only guarantees the geometry is a
    // number.
    at: (t: number) => {
      const v = Number.isFinite(t) ? t : t === Number.POSITIVE_INFINITY ? hi : lo;
      return ((Math.min(Math.max(v, lo), hi) - lo) / span) * 100;
    },
  };
}

/**
 * One 100-wide strip. The geometry is data — where a window sits on a timeline —
 * so it is expressed in SVG attributes rather than in a style attribute, and the
 * colours are token references so the register themes with everything else.
 */
function Strip({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="overflow-hidden rounded-(--radius-sm)">
      <svg
        viewBox="0 0 100 10"
        preserveAspectRatio="none"
        role="img"
        aria-label={label}
        className="block h-[22px] w-full"
      >
        {children}
      </svg>
    </div>
  );
}

export function Authority() {
  const register = useResource<Grant[]>('/authorisations');
  const grants = useMyGrants();
  const now = Date.now();

  const [upn, setUpn] = useState('');
  const [action, setAction] = useState<AuthAction>('CAPTURE');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // What the last Grant recorded (audit finding 15): shown until the next submit or prefill.
  const [saved, setSaved] = useState<string | null>(null);

  // The roster's Grant shortcut only FILLS the form (account and action); the dates, the notes and
  // the press of Grant stay a person's, and the POST is the same one (rule 28).
  const upnInput = useRef<HTMLInputElement>(null);
  const prefill = useCallback((account: string, a: RosterGrantAction) => {
    setUpn(account);
    setAction(a);
    setRefusal(null);
    setSaved(null);
    upnInput.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    upnInput.current?.focus();
  }, []);

  const hint = grantHint(grants, 'AUTH_ADMIN', 'Making a grant');
  const canGrant = hint === null;

  // Ending a grant (UX-33): an in-page confirm panel, never window.confirm, which a tablet's
  // browser may suppress and which cannot carry the coverage warning.
  const [ending, setEnding] = useState<LedgerRow | null>(null);
  const [endWhen, setEndWhen] = useState<'now' | 'day'>('day');
  const [endDay, setEndDay] = useState('');
  const [endSaving, setEndSaving] = useState(false);
  const [endRefusal, setEndRefusal] = useState<string | null>(null);
  const [endSaved, setEndSaved] = useState<string | null>(null);
  const endPanel = useRef<HTMLHeadingElement>(null);
  const today = localDate(now) ?? '';
  const openEnd = useCallback((g: LedgerRow) => {
    setEnding(g);
    setEndWhen('day');
    setEndDay(localDate(Date.now()) ?? '');
    setEndRefusal(null);
    setEndSaved(null);
    // After the panel renders, so there is a heading to move to.
    requestAnimationFrame(() => {
      endPanel.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      endPanel.current?.focus();
    });
  }, []);
  const closeEnd = useCallback(() => {
    const id = ending?.id;
    setEnding(null);
    setEndRefusal(null);
    if (id !== undefined) document.getElementById(`end-grant-${id}`)?.focus();
  }, [ending]);

  const all = rows<Grant>(register.data);
  // The register has actually answered. Coverage is reported only once it has:
  // an empty list while the fetch is in flight looks exactly like an empty
  // register, and "nobody holds RELEASE" on every load is an alarm people learn
  // to ignore.
  const registerRead = register.data !== null && register.error === null;

  // EVERY action, including the ones with no grants at all. Filtering those out
  // hid the worst case behind the same silence as the healthy case: an action
  // nobody has ever been granted is not a tidy empty section, it is an action
  // nobody can exercise.
  const byAction = useMemo(() => {
    return ACTIONS.map((a) => {
      const mine = all
        .filter((g) => g.action === a)
        .sort((x, y) => Date.parse(x.effectiveFrom) - Date.parse(y.effectiveFrom));
      const heldNow = mine.length > 0 && coveredAt(mine, now);
      const end = lastEnd(mine);
      return {
        action: a,
        level: COVERAGE[a],
        grants: mine,
        // Only gaps that have not closed yet (UX-14): a hole last August is history, and listing
        // it on every load kept the box amber for ever, the same fault as CONCESSION's line.
        gaps: coverageGaps(mine).filter((g) => g.to > now),
        heldNow,
        endsSoon: heldNow && end !== null && end - now <= LOOK_AHEAD_MS ? end : null,
      };
    });
  }, [all, now]);

  const ledger = useMemo<LedgerRow[]>(() => {
    const at = Date.now();
    return all.map((g) => {
      const start = Date.parse(g.effectiveFrom);
      const end = g.effectiveTo ? Date.parse(g.effectiveTo) : Number.POSITIVE_INFINITY;
      return { ...g, state: start <= at && at < end ? 'in force' : start > at ? 'future' : 'lapsed' };
    });
  }, [all]);

  const columns = useMemo(() => ledgerColumnsFor(canGrant, openEnd), [canGrant, openEnd]);

  async function endGrant(e: FormEvent) {
    e.preventDefault();
    if (!canGrant || !ending) return;
    const effectiveTo = endInstant(endWhen, endDay);
    if (effectiveTo === undefined) {
      setEndRefusal('Choose the last day the grant is in force.');
      return;
    }
    // A day already gone would backdate the end over work done under the grant (endInstant).
    if (endWhen === 'day' && endDay < today) {
      setEndRefusal('That day has passed. Choose today or a later day, or end it now.');
      return;
    }
    setEndSaving(true);
    setEndRefusal(null);
    setEndSaved(null);
    try {
      // The same POST the API has always had for this: a body with an id ends that grant. No
      // effectiveTo means the server's now. The person ending it is the token's (the event's
      // byUpn), never a field here, as with the granter.
      await api.post('/authorisations', effectiveTo === null ? { id: ending.id } : { id: ending.id, effectiveTo });
      setEndSaved(
        `Grant ${ending.id} ended: ${actionWithCode(ending.action)} for ${ending.upn}, ${
          effectiveTo === null ? 'from now' : `in force to the end of ${endDay}`
        }.`,
      );
      setEnding(null);
      // Reloaded so coverage, gaps and the roster's holders are recomputed from what the server holds.
      register.reload();
    } catch (err: unknown) {
      // The route's own sentences (already ends earlier, before it came into force), as written.
      setEndRefusal(serverMessage(err));
    } finally {
      setEndSaving(false);
    }
  }

  const endAt =
    ending === null
      ? null
      : endWhen === 'now'
        ? now
        : (() => {
            const iso = endInstant('day', endDay);
            return iso ? Date.parse(iso) : null;
          })();
  const endLeavesNobody = ending !== null && endAt !== null && !heldByOthersAt(all, ending, endAt);

  async function makeGrant(e: FormEvent) {
    e.preventDefault();
    if (!canGrant) return;

    // LOCAL midnight, not the raw date string. A bare "2026-09-16" is parsed as
    // UTC midnight, which at UTC+10 is 10am here — a grant made to start today
    // would not be in force for the morning shift, and the gate refuses rather
    // than warns, so the operator is simply turned away with no clue why.
    const fromInstant = localInstant(from);
    if (!fromInstant) {
      setRefusal('A grant needs a date it comes into force.');
      return;
    }
    const toInstant = to === '' ? null : localInstant(to);

    setSaving(true);
    setRefusal(null);
    setSaved(null);
    try {
      // AuthorisedByUpn is NOT sent: the granter is the signed-in person on the
      // validated token, and a value posted from a form would be a claim about
      // who signed rather than a record of it.
      const account = upn.trim().toLowerCase();
      await api.post('/authorisations', {
        upn: account,
        action,
        effectiveFrom: fromInstant,
        effectiveTo: toInstant,
        notes: notes.trim() || null,
      });
      setSaved(
        `Grant recorded: ${actionWithCode(action)} for ${account} from ${dayOf(fromInstant)}${
          toInstant ? ` until ${dayOf(toInstant)}` : ', open-ended'
        }.`,
      );
      setUpn('');
      setFrom('');
      setTo('');
      setNotes('');
      register.reload();
    } catch (err: unknown) {
      setRefusal(serverMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-(--s5)">
      <header>
        <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Authority</h1>
        <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
          Who may do what, and from when. Overlap grants at a handover: a gap leaves nobody able to act.
        </p>
      </header>


      {/* The whole point of the page, said first. A gap in RELEASE is the one that
          strands held product, so it is named rather than left to be inferred.
          Amber only for the actions a day's work needs (COVERAGE, UX-14); the on-demand ones are
          still named, in a muted line, because "nobody holds CONCESSION" is true and somebody may
          need to know it, but it is not a defect. Retired actions are not listed. */}
      {registerRead && byAction.some((a) => a.level === 'must' && (a.gaps.length > 0 || !a.heldNow || a.endsSoon !== null)) ? (
        <section
          aria-label="Coverage problems"
          className="rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s4)"
        >
          <h2 className="m-0 text-(length:--fs-md) font-bold text-(--warn)">
            Coverage
          </h2>
          <ul className="m-0 mt-(--s2) list-disc pl-(--s5) text-(length:--fs-sm)">
            {byAction
              .filter((a) => a.level === 'must' && !a.heldNow)
              .map((a) => (
                <li key={`now-${a.action}`} className="font-semibold">
                  Nobody holds {actionWithCode(a.action)} right now
                  {a.grants.length === 0 ? ' — it has never been granted to anyone' : ''}.
                  {a.action === 'RELEASE'
                    ? ' Held product cannot be released until somebody does.'
                    : ''}
                </li>
              ))}
            {byAction
              .filter((a) => a.level === 'must' && a.endsSoon !== null)
              .map((a) => (
                <li key={`ends-${a.action}`}>
                  {actionWithCode(a.action)}: every grant ends {day(a.endsSoon!)}. Nobody holds it after that unless a
                  new grant is made.
                </li>
              ))}
            {byAction
              .filter((a) => a.level === 'must')
              .flatMap((a) =>
                a.gaps.map((g, i) => (
                  <li key={`gap-${a.action}-${i}`}>
                    {actionWithCode(a.action)}: nobody holds it from {day(g.from)} to {day(g.to)} — a gap between two
                    grants, not an overlap.
                  </li>
                )),
              )}
          </ul>
        </section>
      ) : null}

      {registerRead && byAction.some((a) => a.level === 'on-demand' && (!a.heldNow || a.gaps.length > 0)) ? (
        <p className="m-0 text-(length:--fs-xs) text-(--muted)">
          Granted when needed, not a gap:{' '}
          {byAction
            .filter((a) => a.level === 'on-demand' && (!a.heldNow || a.gaps.length > 0))
            .map((a) =>
              !a.heldNow
                ? `nobody holds ${actionWithCode(a.action)} now${a.grants.length === 0 ? ' (never granted)' : ''}`
                : `${actionWithCode(a.action)} is not held from ${day(a.gaps[0].from)} to ${day(a.gaps[0].to)}`,
            )
            .join('; ')}
          .
        </p>
      ) : null}

      {/* The form under Coverage (UI/UX audit, 31 Oct 2026, UX-51): it sat below every timeline
          and the roster, at y=3745 of a 4809px page, so the gap the Coverage box names was a
          long scroll away from the one control that closes it. */}
      <section className="rounded-(--radius) border border-(--border) bg-(--card) shadow-card p-(--s4)">
        <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">Grant an authority</h2>
        <form onSubmit={makeGrant} className="mt-(--s3) flex flex-wrap items-end gap-(--s3)">
          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="grant-upn" className="text-[13px] font-semibold text-muted-foreground">
              Account
            </label>
            {/* Typed, because there is no Person table: the directory address IS
                the identity this app records against. */}
            <input
              id="grant-upn"
              ref={upnInput}
              type="email"
              required
              value={upn}
              onChange={(e) => setUpn(e.target.value)}
              placeholder="name@majans.com"
              className={`min-h-[44px] min-w-[260px] rounded-(--radius-sm) border border-(--border-strong) bg-(--card) px-(--s3) text-(length:--fs-sm) ${officeFocus}`}
            />
          </div>

          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="grant-action" className="text-[13px] font-semibold text-muted-foreground">
              Action
            </label>
            <select
              id="grant-action"
              value={action}
              onChange={(e) => setAction(e.target.value as AuthAction)}
              className={`min-h-[44px] rounded-(--radius-sm) border border-(--border-strong) bg-(--card) px-(--s3) text-(length:--fs-sm) ${officeFocus}`}
            >
              {/* Words first, the code kept for the register and the gateway; a retired action is
                  not offered (UI/UX audit, 31 Oct 2026, UX-17: the picker listed ARTWORK_SIGNOFF). */}
              {ACTIONS.filter((a) => !RETIRED_ACTIONS.has(a)).map((a) => (
                <option key={a} value={a}>
                  {actionWithCode(a)}
                </option>
              ))}
            </select>
            <span className="max-w-[320px] text-(length:--fs-xs) text-(--muted)">{ACTION_LABEL[action].meaning}</span>
          </div>

          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="grant-from" className="text-[13px] font-semibold text-muted-foreground">
              Effective from
            </label>
            <input
              id="grant-from"
              type="date"
              required
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={`min-h-[44px] rounded-(--radius-sm) border border-(--border-strong) bg-(--card) px-(--s3) text-(length:--fs-sm) ${officeFocus}`}
            />
          </div>

          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="grant-to" className="text-[13px] font-semibold text-muted-foreground">
              Effective to (blank = open)
            </label>
            <input
              id="grant-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className={`min-h-[44px] rounded-(--radius-sm) border border-(--border-strong) bg-(--card) px-(--s3) text-(length:--fs-sm) ${officeFocus}`}
            />
          </div>

          <div className="flex min-w-[240px] flex-1 flex-col gap-(--s1)">
            <label htmlFor="grant-notes" className="text-[13px] font-semibold text-muted-foreground">
              Notes
            </label>
            <input
              id="grant-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why this grant exists"
              className={`min-h-[44px] w-full rounded-(--radius-sm) border border-(--border-strong) bg-(--card) px-(--s3) text-(length:--fs-sm) ${officeFocus}`}
            />
          </div>

          <span title={hint ?? 'Record this grant'}>
            <Button variant="default"
              type="submit"
              disabled={!canGrant || saving}
              aria-describedby="grant-hint"
            >
              {saving ? 'Granting…' : 'Grant'}
            </Button>
          </span>
        </form>

        <p id="grant-hint" aria-live="polite" className="m-0 mt-(--s2) text-(length:--fs-xs) text-(--muted)">
          {hint ?? 'Recorded against your account as the granter.'}
        </p>
        {refusal ? (
          <p role="alert" className="m-0 mt-(--s2) text-(length:--fs-sm) font-semibold text-(--warn)">
            {refusal}
          </p>
        ) : null}
        {saved ? (
          <div className="mt-(--s2)">
            <Saved>{saved}</Saved>
          </div>
        ) : null}
      </section>

      {/* A timeline needs something to draw. An action with no grants has already
          been named in the coverage list above, which is the part that matters. */}
      {byAction.filter((group) => group.grants.length > 0).map((group) => {
        const axis = axisFor(group.grants, now);
        return (
          <section key={group.action} className="rounded-(--radius) border border-(--border) bg-(--card) shadow-card p-(--s4)">
            <div className="flex flex-wrap items-baseline gap-(--s3)">
              <h2 className="m-0 text-(length:--fs-md) font-bold">{actionWithCode(group.action)}</h2>
              <span
                className={`text-(length:--fs-xs) font-bold uppercase tracking-[0.06em] ${
                  group.heldNow ? 'text-(--ok)' : 'text-(--warn)'
                }`}
              >
                {group.heldNow ? 'held now' : 'held by nobody now'}
              </span>
            </div>

            {/* Coverage first: amber ground is "nobody", green is "somebody". The
                question this answers is not who holds it, but whether anyone does. */}
            {/* One column on a phone (UI/UX audit, 31 Oct 2026, UX-32): at 390px the fixed
                180px + 170px tracks left the strip a 0px middle column, so every bar vanished and
                the dates ran off the card. Stacked, each grant reads name, bar, dates. */}
            <div className="mt-(--s3) grid grid-cols-1 items-center gap-(--s3) sm:grid-cols-[180px_minmax(0,1fr)_170px]">
              <span className="text-[13px] font-semibold text-muted-foreground">
                Coverage
              </span>
              <Strip label={`Coverage for ${group.action}`}>
                <rect x="0" y="0" width="100" height="10" fill="var(--warn-bg)" />
                {intervalsOf(group.grants).map((i, k) => (
                  <rect
                    key={k}
                    x={axis.at(i.from)}
                    y="0"
                    width={Math.max(0, axis.at(i.to) - axis.at(i.from))}
                    height="10"
                    fill="var(--ok-bg)"
                  />
                ))}
                <rect x={axis.at(now) - 0.2} y="0" width="0.4" height="10" fill="var(--fg)" />
              </Strip>
              <span className="font-mono text-(length:--fs-xs) text-(--muted-2)">
                {coverageSpan(group.grants)}
              </span>

              {group.grants.map((g) => {
                const start = Date.parse(g.effectiveFrom);
                const end = g.effectiveTo ? Date.parse(g.effectiveTo) : Number.POSITIVE_INFINITY;
                // ⚠ A GRANT WITH NO PARSEABLE START HAS NO PLACE ON THE AXIS. The
                // coverage strip above is safe because intervalsOf() drops such
                // rows; this per-grant row parsed the date raw, so an undated
                // grant produced x="NaN" and width="NaN" on the bar — a rendering
                // error in the console on every paint, and a bar that silently
                // did not exist. The column is NOT NULL in dbo.Authorisation, so
                // the database cannot produce this; a stub, a hand-edited row or
                // a future API change can, and the failure must be visible rather
                // than a console error nobody reads. The row is drawn with its
                // baseline and the raw value in the label, and no bar.
                const dated = Number.isFinite(start);
                const live = dated && start <= now && now < end;
                // A Fragment, so the three cells stay direct children of the grid.
                return (
                  <Fragment key={g.id}>
                    <span className="truncate text-(length:--fs-sm) font-semibold" title={g.upn}>
                      {g.upn}
                    </span>
                    <Strip
                      label={
                        dated
                          ? `${g.upn}, ${dayOf(g.effectiveFrom)} to ${dayOf(g.effectiveTo)}`
                          : `${g.upn}, start date unreadable: ${String(g.effectiveFrom)}`
                      }
                    >
                      <rect x="0" y="4" width="100" height="2" fill="var(--subtle)" />
                      {dated ? (
                        <rect
                          x={axis.at(start)}
                          y="1"
                          width={Math.max(0.5, axis.at(end) - axis.at(start))}
                          height="8"
                          fill={live ? 'var(--ok-line)' : 'var(--border-strong)'}
                        />
                      ) : null}
                      <rect x={axis.at(now) - 0.2} y="0" width="0.4" height="10" fill="var(--fg)" />
                    </Strip>
                    <span className="font-mono text-(length:--fs-xs) text-(--muted)">
                      {dayOf(g.effectiveFrom)} → {dayOf(g.effectiveTo)}
                    </span>
                  </Fragment>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* The grants in force, so the roster can name holders it matched to nobody (UX-06). Null
          until the register has answered: an unread register is not "everyone matched". */}
      <ProductionRoster
        grants={grants}
        onGrant={prefill}
        inForce={registerRead ? ledger.filter((g) => g.state === 'in force') : null}
      />

      <section>
        <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">Register</h2>
        {endSaved ? (
          <div className="mt-(--s2)">
            <Saved>{endSaved}</Saved>
          </div>
        ) : null}
        {/* The confirm panel for "End grant" (UX-33): what ends, when, and what it leaves. */}
        {ending ? (
          <section
            aria-labelledby="end-grant-heading"
            className="mt-(--s2) rounded-(--radius) border border-(--border-strong) bg-(--card) shadow-card p-(--s4)"
          >
            <h3
              id="end-grant-heading"
              ref={endPanel}
              tabIndex={-1}
              className={`m-0 text-(length:--fs-md) font-bold ${officeFocus}`}
            >
              End grant {ending.id}: {actionWithCode(ending.action)} for {ending.upn}
            </h3>
            <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
              In force {dayOf(ending.effectiveFrom)} → {dayOf(ending.effectiveTo)}. The row is kept: ending
              a grant brings its end date forward, and the register shows it lapsed from then.
            </p>
            <form onSubmit={endGrant} className="mt-(--s3) flex flex-wrap items-end gap-(--s3)">
              <fieldset className="m-0 flex flex-col gap-(--s2) border-0 p-0">
                <legend className="text-[13px] font-semibold text-muted-foreground">When it stops</legend>
                <label className="flex items-center gap-(--s2) text-(length:--fs-sm)">
                  <input
                    type="radio"
                    name="end-when"
                    checked={endWhen === 'day'}
                    onChange={() => setEndWhen('day')}
                  />
                  At the end of
                  <input
                    type="date"
                    aria-label="Last day in force"
                    min={today}
                    value={endDay}
                    disabled={endWhen !== 'day'}
                    onChange={(e) => setEndDay(e.target.value)}
                    className={`min-h-[44px] rounded-(--radius-sm) border border-(--border-strong) bg-(--card) px-(--s3) text-(length:--fs-sm) ${officeFocus}`}
                  />
                </label>
                <label className="flex items-center gap-(--s2) text-(length:--fs-sm)">
                  <input
                    type="radio"
                    name="end-when"
                    checked={endWhen === 'now'}
                    onChange={() => setEndWhen('now')}
                  />
                  Now: they cannot act on it again from this moment
                </label>
              </fieldset>
              <Button variant="default" type="submit" disabled={endSaving}>
                {endSaving ? 'Ending…' : 'End grant'}
              </Button>
              <Button variant="outline" type="button" onClick={closeEnd} disabled={endSaving}>
                Cancel
              </Button>
            </form>
            {endLeavesNobody ? (
              <p className="m-0 mt-(--s2) text-(length:--fs-sm) font-semibold text-(--warn)">
                Nobody else holds {actionWithCode(ending.action)} then. Make the next grant first, so they overlap.
                {ending.action === 'AUTH_ADMIN' ? ' Without one, nobody can make a grant from this page again.' : ''}
              </p>
            ) : null}
            {endRefusal ? (
              <p role="alert" className="m-0 mt-(--s2) text-(length:--fs-sm) font-semibold text-(--warn)">
                {endRefusal}
              </p>
            ) : null}
          </section>
        ) : null}
        <div className="mt-(--s2)">
          <DataTable
            data={ledger}
            columns={columns}
            caption="Every authorisation, current and lapsed"
            getRowId={(g) => String(g.id)}
            pageSize={50}
            search="Search account, action or notes"
            loading={register.loading}
            error={register.error}
            onRetry={register.reload}
            empty="No grants recorded. Nobody can capture a reading until somebody holds CAPTURE."
          />
        </div>
      </section>
    </div>
  );
}

export default Authority;
